import {
  validateTextSizeForLevel,
  LEVEL_TEXT_SIZE_RULES,
  TEXT_SIZE_CONFIGS,
} from "@lingoshade/domain";
import {
  Check as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Chip,
  Box,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import React, { useState } from "react";

import { useAppDispatch } from "../store";
import { createText, fetchAdminTexts } from "../store/slices/adminTextsSlice";
import { SUPPORTED_LANGUAGES } from "../utils/languages";

import type { LevelCode, TextSize } from "@lingoshade/domain";

interface CreateTextDialogProps {
  open: boolean;
  onClose: () => void;
}

const DIFFICULTY_LEVELS = [
  { value: "BEGINNER", label: "Beginner (A1)" },
  { value: "ELEMENTARY", label: "Elementary (A2)" },
  { value: "INTERMEDIATE", label: "Intermediate (B1)" },
  { value: "UPPER_INTERMEDIATE", label: "Upper Intermediate (B2)" },
  { value: "ADVANCED", label: "Advanced (C1)" },
  { value: "PROFICIENT", label: "Proficient (C2)" },
];

// Get available text sizes for a given level using domain rules
const getAvailableTextSizes = (
  level: string,
): Array<{ value: string; label: string }> => {
  const allowedSizes = LEVEL_TEXT_SIZE_RULES[level as LevelCode] || ["SHORT"];
  const allSizes = [
    { value: "SHORT", label: TEXT_SIZE_CONFIGS.SHORT.description },
    { value: "MEDIUM", label: TEXT_SIZE_CONFIGS.MEDIUM.description },
    { value: "LONG", label: TEXT_SIZE_CONFIGS.LONG.description },
  ];

  return allSizes.filter((size) =>
    allowedSizes.includes(size.value as TextSize),
  );
};

// Преобразуем SUPPORTED_LANGUAGES в нужный формат с флагами
const LANGUAGE_FLAGS: Record<string, string> = {
  en: "🇺🇸",
  es: "🇪🇸",
  fr: "🇫🇷",
  de: "🇩🇪",
  it: "🇮🇹",
  ru: "🇷🇺",
  pl: "🇵🇱",
  zh: "🇨🇳",
  ja: "🇯🇵",
  ko: "🇰🇷",
  el: "🇬🇷",
  th: "🇹🇭",
  tr: "🇹🇷",
  uk: "🇺🇦",
};

const AVAILABLE_LANGUAGES = SUPPORTED_LANGUAGES.map((lang) => ({
  code: lang.code,
  name: lang.name,
  flag: LANGUAGE_FLAGS[lang.code] || "�",
}));

interface GenerationResult {
  language: string;
  status: "pending" | "generating" | "success" | "error";
  textId?: string;
  error?: string;
}

const CreateTextDialog: React.FC<CreateTextDialogProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    contentPrompt: "",
    level: "INTERMEDIATE",
    textSize: "MEDIUM",
    languages: [] as string[],
    freeUserAccess: false,
    isPublic: true,
  });

  // Client-side validation state
  const [validationErrors, setValidationErrors] = useState<{
    contentPrompt?: string;
    textSize?: string;
    languages?: string;
  }>({});

  // Get available text sizes for current level
  const availableTextSizes = getAvailableTextSizes(formData.level);

  // Form validation using domain rules
  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {};

    // Content prompt validation
    if (!formData.contentPrompt?.trim()) {
      errors.contentPrompt = "Content prompt is required";
    } else if (formData.contentPrompt.trim().length < 10) {
      errors.contentPrompt =
        "Content prompt must be at least 10 characters long";
    } else if (formData.contentPrompt.trim().length > 1000) {
      errors.contentPrompt = "Content prompt cannot exceed 1000 characters";
    }

    // Text size validation using domain rules
    if (!formData.textSize) {
      errors.textSize = "Text size is required";
    } else {
      const validation = validateTextSizeForLevel(
        formData.level as LevelCode,
        formData.textSize as TextSize,
      );
      if (!validation.isValid) {
        errors.textSize = `${formData.textSize} size is not available for ${formData.level} level. Available sizes: ${validation.allowedSizes.join(", ")}`;
      }
    }

    // Languages validation
    if (formData.languages.length === 0) {
      errors.languages = "At least one language must be selected";
    } else if (formData.languages.length > 20) {
      errors.languages = "Cannot select more than 20 languages at once";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const [isGenerating, setIsGenerating] = useState(false);
  const [generationResults, setGenerationResults] = useState<
    GenerationResult[]
  >([]);

  const handleLanguageToggle = (languageCode: string): void => {
    setFormData((prev) => {
      const newLanguages = prev.languages.includes(languageCode)
        ? prev.languages.filter((l) => l !== languageCode)
        : [...prev.languages, languageCode];

      // Clear languages validation error if languages are selected
      if (validationErrors.languages && newLanguages.length > 0) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.languages;
          return newErrors;
        });
      }

      return {
        ...prev,
        languages: newLanguages,
      };
    });
  };

  // Handle form field changes with validation and level-size compatibility
  const handleInputChange = (field: string, value: string): void => {
    const newFormData = {
      ...formData,
      [field]: value,
    };

    // If level changes, validate text size compatibility
    if (field === "level") {
      const validation = validateTextSizeForLevel(
        value as LevelCode,
        formData.textSize as TextSize,
      );
      if (!validation.isValid) {
        // Automatically select the first available size for the new level
        const allowedSizes = LEVEL_TEXT_SIZE_RULES[value as LevelCode] || [
          "SHORT",
        ];
        newFormData.textSize = allowedSizes[0];
      }
    }

    // Clear validation errors for changed fields
    const clearedErrors = { ...validationErrors };
    if (field === "contentPrompt" && clearedErrors.contentPrompt) {
      delete clearedErrors.contentPrompt;
      setValidationErrors(clearedErrors);
    }
    if (field === "textSize" && clearedErrors.textSize) {
      delete clearedErrors.textSize;
      setValidationErrors(clearedErrors);
    }

    setFormData(newFormData);
  };

  const validateStep = (stepIndex: number): boolean => {
    switch (stepIndex) {
      case 0:
        return formData.contentPrompt.trim().length > 10;
      case 1:
        return formData.languages.length > 0;
      default:
        return true;
    }
  };

  const handleNext = (): void => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleBack = (): void => {
    setStep(step - 1);
  };

  const handleSubmit = async (): Promise<void> => {
    // Clear previous validation errors
    setValidationErrors({});

    // Validate form using domain rules
    if (!validateForm()) {
      return;
    }

    setIsGenerating(true);
    setStep(2); // Move to generation step

    // Initialize generation results
    const results: GenerationResult[] = formData.languages.map((lang) => ({
      language: lang,
      status: "pending",
    }));
    setGenerationResults(results);

    // Generate texts one by one with delays to avoid API limits
    for (let i = 0; i < formData.languages.length; i++) {
      const language = formData.languages[i];

      // Update status to generating
      setGenerationResults((prev) =>
        prev.map((r) =>
          r.language === language ? { ...r, status: "generating" } : r,
        ),
      );

      try {
        const result = await dispatch(
          createText({
            contentPrompt: formData.contentPrompt,
            level: formData.level,
            textSize: formData.textSize,
            languages: [language], // Generate one at a time
            freeUserAccess: formData.freeUserAccess,
            isPublic: formData.isPublic,
          }),
        ).unwrap();

        // Update success status
        setGenerationResults((prev) =>
          prev.map((r) =>
            r.language === language
              ? { ...r, status: "success", textId: result.text.id }
              : r,
          ),
        );
      } catch (err: unknown) {
        // Safe extraction of message without using any
        let message = "Generation failed";
        if (typeof err === "object" && err && "message" in err) {
          // @ts-expect-error runtime check guarantees presence
          message = err.message || message;
        }
        setGenerationResults((prev) =>
          prev.map((r) =>
            r.language === language
              ? {
                  ...r,
                  status: "error",
                  error: message,
                }
              : r,
          ),
        );
      }

      // Add delay between generations (except for the last one)
      if (i < formData.languages.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 2000)); // 2 second delay
      }
    }

    setIsGenerating(false);

    // Refresh the texts list
    dispatch(fetchAdminTexts({ page: 1, limit: 50 }));
  };

  const handleClose = (): void => {
    if (!isGenerating) {
      setStep(0);
      setFormData({
        contentPrompt: "",
        level: "INTERMEDIATE",
        textSize: "MEDIUM",
        languages: [],
        freeUserAccess: false,
        isPublic: true,
      });
      setGenerationResults([]);
      onClose();
    }
  };

  const getLanguageName = (code: string): string => {
    const lang = AVAILABLE_LANGUAGES.find((l) => l.code === code);
    return lang ? `${lang.flag} ${lang.name}` : code.toUpperCase();
  };

  const getStatusIcon = (
    status: GenerationResult["status"],
  ): React.ReactNode => {
    switch (status) {
      case "success":
        return <CheckIcon color="success" />;
      case "error":
        return <ErrorIcon color="error" />;
      case "generating":
        return <LinearProgress sx={{ width: 20 }} />;
      default:
        return <WarningIcon color="action" />;
    }
  };

  const successCount = generationResults.filter(
    (r) => r.status === "success",
  ).length;
  const errorCount = generationResults.filter(
    (r) => r.status === "error",
  ).length;
  const isCompleted = successCount + errorCount === formData.languages.length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Text</DialogTitle>

      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          <Step>
            <StepLabel>Content & Settings</StepLabel>
          </Step>
          <Step>
            <StepLabel>Select Languages</StepLabel>
          </Step>
          <Step>
            <StepLabel>Generate Texts</StepLabel>
          </Step>
        </Stepper>

        {/* Step 0: Content and Settings */}
        {step === 0 && (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Content Prompt"
                placeholder="Describe the topic and content you want to generate (e.g., 'A story about a person traveling to Paris and ordering food at a restaurant')"
                value={formData.contentPrompt}
                onChange={(e) =>
                  handleInputChange("contentPrompt", e.target.value)
                }
                error={!!validationErrors.contentPrompt}
                helperText={
                  validationErrors.contentPrompt ||
                  "Provide a clear description of the content you want to generate"
                }
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                select
                label="Difficulty Level"
                value={formData.level}
                onChange={(e) => handleInputChange("level", e.target.value)}
              >
                {DIFFICULTY_LEVELS.map((level) => (
                  <MenuItem key={level.value} value={level.value}>
                    {level.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                select
                label="Text Length"
                value={formData.textSize}
                onChange={(e) => handleInputChange("textSize", e.target.value)}
                error={!!validationErrors.textSize}
                helperText={validationErrors.textSize}
              >
                {availableTextSizes.map((size) => (
                  <MenuItem key={size.value} value={size.value}>
                    {size.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.freeUserAccess}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        freeUserAccess: e.target.checked,
                      })
                    }
                  />
                }
                label="Allow free user access"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPublic}
                    onChange={(e) =>
                      setFormData({ ...formData, isPublic: e.target.checked })
                    }
                  />
                }
                label="Make text public"
              />
            </Grid>
          </Grid>
        )}

        {/* Step 1: Language Selection */}
        {step === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Languages ({formData.languages.length} selected)
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Select one or more languages to generate texts for. Texts will be
              generated sequentially with delays to respect API limits.
            </Typography>

            {/* Languages validation error */}
            {validationErrors.languages && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {validationErrors.languages}
              </Alert>
            )}

            <Grid container spacing={1}>
              {AVAILABLE_LANGUAGES.map((language) => (
                <Grid size={{ xs: 6, sm: 4, md: 3 }} key={language.code}>
                  <Chip
                    label={`${language.flag} ${language.name}`}
                    onClick={() => handleLanguageToggle(language.code)}
                    color={
                      formData.languages.includes(language.code)
                        ? "primary"
                        : "default"
                    }
                    variant={
                      formData.languages.includes(language.code)
                        ? "filled"
                        : "outlined"
                    }
                    sx={{ width: "100%", justifyContent: "flex-start" }}
                  />
                </Grid>
              ))}
            </Grid>

            {formData.languages.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Please select at least one language to continue.
              </Alert>
            )}
          </Box>
        )}

        {/* Step 2: Generation Progress */}
        {step === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Generating Texts
            </Typography>

            {isGenerating && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Generating texts with 2-second delays between languages to
                respect API limits...
              </Alert>
            )}

            <List>
              {generationResults.map((result) => (
                <ListItem key={result.language}>
                  <ListItemIcon>{getStatusIcon(result.status)}</ListItemIcon>
                  <ListItemText
                    primary={getLanguageName(result.language)}
                    secondary={
                      result.status === "generating"
                        ? "Generating..."
                        : result.status === "success"
                          ? "Generated successfully"
                          : result.status === "error"
                            ? `Error: ${result.error}`
                            : "Waiting..."
                    }
                  />
                </ListItem>
              ))}
            </List>

            {isCompleted && (
              <Alert
                severity={errorCount > 0 ? "warning" : "success"}
                sx={{ mt: 2 }}
              >
                {errorCount > 0
                  ? `Generation completed with ${successCount} successful and ${errorCount} failed attempts.`
                  : `All ${successCount} texts generated successfully!`}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isGenerating}>
          {isCompleted ? "Close" : "Cancel"}
        </Button>

        {step > 0 && step < 2 && (
          <Button onClick={handleBack} disabled={isGenerating}>
            Back
          </Button>
        )}

        {step < 1 && (
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={!validateStep(step)}
          >
            Next
          </Button>
        )}

        {step === 1 && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={formData.languages.length === 0 || isGenerating}
          >
            Generate Texts ({formData.languages.length})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateTextDialog;
